/**
 * Central CPF edit contract.
 * Intended behavior: show === write for a given mode/cell.
 * Phase B–C: wired to editClick, cells, OptionsCell, EditRowCell, add-role.
 * @see .cursor/rules/ctm-care-plan-frequency-edit-paths.mdc
 */
import { PRN } from '@mummssoftware/utils/orders/orders-constants';

import {
  Frequency,
  Effective,
  SpecificDays,
  PlusPRN,
  CPF_EDIT_MODE,
  CPF_SAVE_VIA,
  CPF_INLINE_EDIT_DENY,
  CpfEditMode,
  CpfSaveViaKind,
  CpfInlineEditDenyReason,
} from './utils/constant';
import {
  hasEndDate,
  isAideOrVolunteerRoleGroup,
  isFreqNotEditable,
} from './components/cells/utils';

export type { CpfEditMode, CpfInlineEditDenyReason };
export { CPF_EDIT_MODE, CPF_SAVE_VIA, CPF_INLINE_EDIT_DENY };

export type CpfEditColumn =
  | typeof Frequency
  | typeof Effective
  | typeof SpecificDays
  | typeof PlusPRN;

export type CpfSaveVia = CpfSaveViaKind | null;

export type CellEditRights = {
  show: boolean;
  write: boolean;
  saveVia: CpfSaveVia;
};

export type WidgetEditContext = {
  isCPFAgency: boolean;
  isPRNOrderAgency: boolean;
  VFordersMode: boolean;
};

export type InlineOpenContext = {
  mode: CpfEditMode;
  column: CpfEditColumn;
  carePlan?: mumms.ProcessedCarePlan | null;
  assignable?: boolean;
  isDoubleClick: boolean;
  smallWidget?: boolean;
  viewModePrint?: boolean;
  /** Role group currently locked for inline edit, if any */
  editingRoleGroupId?: string | number | null;
  roleGroupId: string | number;
};

export type CellRightsContext = {
  mode: CpfEditMode;
  column: CpfEditColumn;
  /** True when this row is the one being edited (dialog or inline) */
  isActiveRow: boolean;
  carePlan?: mumms.ProcessedCarePlan | null;
  readOnly?: boolean;
  /** Period used for Specific Days lock (editPeriod or period) */
  period?: string | null;
};

/**
 * Widget-level mode from bins + orders flag.
 * - ORDER: CPF order in progress (pencil / dialog)
 * - INLINE_FULL: PRN agency, idle → Frequency / Effective / Days
 * - INLINE_DAYS: idle without PRN → Specific Days only
 * - NONE: reserved (e.g. future hard lock); idle always at least INLINE_DAYS today
 */
export const getWidgetEditMode = ({
  isPRNOrderAgency,
  VFordersMode,
}: WidgetEditContext): CpfEditMode => {
  if (VFordersMode) return CPF_EDIT_MODE.ORDER;
  if (isPRNOrderAgency) return CPF_EDIT_MODE.INLINE_FULL;
  return CPF_EDIT_MODE.INLINE_DAYS;
};

export const shouldShowPencilColumn = (mode: CpfEditMode) =>
  mode === CPF_EDIT_MODE.ORDER;

export const shouldShowOptionsColumn = (mode: CpfEditMode) =>
  mode !== CPF_EDIT_MODE.ORDER;

/**
 * Hide row "+" during PRN Orders for Aide/Volunteer when the agency is on
 * prnOrderWidgetBins (same roles that hide +PRN). Edit icon stays.
 */
export const shouldHideOrderAddIcon = ({
  roleGroupId,
  isPRNOrderAgency,
  VFordersMode,
  orderType,
}: {
  roleGroupId: string | number;
  isPRNOrderAgency: boolean;
  VFordersMode: boolean;
  orderType?: string | null;
}) =>
  !!VFordersMode &&
  !!isPRNOrderAgency &&
  orderType === PRN &&
  isAideOrVolunteerRoleGroup(roleGroupId);

/** Matches CarePlanFrequency showAddRole: (!isCPFAgency || VFordersMode) */
export const canShowAddRole = ({
  isCPFAgency,
  VFordersMode,
  smallWidget,
  readOnly,
}: {
  isCPFAgency: boolean;
  VFordersMode: boolean;
  smallWidget?: boolean;
  readOnly?: boolean;
}) => (!isCPFAgency || VFordersMode) && !smallWidget && !readOnly;

const inlineColumnsForMode = (mode: CpfEditMode): CpfEditColumn[] => {
  if (mode === CPF_EDIT_MODE.INLINE_FULL) {
    return [Frequency, Effective, SpecificDays];
  }
  if (mode === CPF_EDIT_MODE.INLINE_DAYS) {
    return [SpecificDays];
  }
  return [];
};

/**
 * May double-click start inline edit on this cell?
 * Does not apply in order mode (pencil path).
 */
export const canStartInlineEdit = ({
  mode,
  column,
  carePlan,
  assignable,
  isDoubleClick,
  smallWidget,
  viewModePrint,
  editingRoleGroupId,
  roleGroupId,
}: InlineOpenContext): { ok: boolean; reason?: CpfInlineEditDenyReason } => {
  if (mode === CPF_EDIT_MODE.ORDER || mode === CPF_EDIT_MODE.NONE) {
    return { ok: false, reason: CPF_INLINE_EDIT_DENY.NOT_INLINE_MODE };
  }
  if (!isDoubleClick) {
    return { ok: false, reason: CPF_INLINE_EDIT_DENY.NOT_DOUBLE_CLICK };
  }
  if (smallWidget || viewModePrint) {
    return { ok: false, reason: CPF_INLINE_EDIT_DENY.VIEW_LOCKED };
  }
  if (!assignable) {
    return { ok: false, reason: CPF_INLINE_EDIT_DENY.NOT_ASSIGNABLE };
  }
  if (hasEndDate(carePlan)) {
    return { ok: false, reason: CPF_INLINE_EDIT_DENY.HAS_END_DATE };
  }
  if (!inlineColumnsForMode(mode).includes(column)) {
    return { ok: false, reason: CPF_INLINE_EDIT_DENY.COLUMN_NOT_ALLOWED };
  }
  if (column === SpecificDays) {
    const periodForDays = carePlan?.editPeriod || carePlan?.period;
    if (isFreqNotEditable(periodForDays || null)) {
      return { ok: false, reason: CPF_INLINE_EDIT_DENY.DAYS_NOT_EDITABLE };
    }
  }
  if (
    editingRoleGroupId != null &&
    String(editingRoleGroupId) !== String(roleGroupId)
  ) {
    return { ok: false, reason: CPF_INLINE_EDIT_DENY.ANOTHER_ROW_EDITING };
  }
  return { ok: true };
};

const denied: CellEditRights = { show: false, write: false, saveVia: null };

/**
 * Per-cell show/write. Intended contract: show === write when allowed.
 * Order dialog and active inline row both get write rights (fixes Way 2 grid bug).
 */
export const getCellEditRights = ({
  mode,
  column,
  isActiveRow,
  carePlan,
  readOnly,
  period,
}: CellRightsContext): CellEditRights => {
  if (readOnly || hasEndDate(carePlan)) {
    return denied;
  }

  if (mode === CPF_EDIT_MODE.ORDER) {
    if (!isActiveRow) return denied;
    // Daily / Refused / etc. — no day picker (same as inline Specific Days lock)
    if (column === SpecificDays) {
      const periodForDays = period || carePlan?.editPeriod || carePlan?.period;
      if (isFreqNotEditable(periodForDays || null)) {
        return denied;
      }
    }
    // PRN hidden for some roles is UI concern; policy allows show/write in order dialog
    return { show: true, write: true, saveVia: CPF_SAVE_VIA.ORDER };
  }

  if (mode === CPF_EDIT_MODE.NONE || !isActiveRow) {
    return denied;
  }

  const allowed = inlineColumnsForMode(mode);
  if (!allowed.includes(column)) {
    return denied;
  }

  if (column === SpecificDays) {
    const periodForDays = period || carePlan?.editPeriod || carePlan?.period;
    if (isFreqNotEditable(periodForDays || null)) {
      return denied;
    }
  }

  // Inline: OptionsCell check → postCarePlan
  return {
    show: true,
    write: true,
    saveVia: CPF_SAVE_VIA.POST_CARE_PLAN,
  };
};

export const getInlineSaveVia = (mode: CpfEditMode): CpfSaveVia =>
  mode === CPF_EDIT_MODE.INLINE_FULL || mode === CPF_EDIT_MODE.INLINE_DAYS
    ? CPF_SAVE_VIA.POST_CARE_PLAN
    : null;

/**
 * Toast copy key (en.json) when a double-click did not start edit.
 * Returns null for silent cases (single click, order mode).
 */
export const getInlineEditDenyToastKey = (
  reason?: CpfInlineEditDenyReason,
): string | null => {
  if (!reason) return null;
  switch (reason) {
    case CPF_INLINE_EDIT_DENY.ANOTHER_ROW_EDITING:
      return 'finishOrCancelCurrentRow';
    case CPF_INLINE_EDIT_DENY.HAS_END_DATE:
      return 'inlineEditDeniedEndDate';
    case CPF_INLINE_EDIT_DENY.NOT_ASSIGNABLE:
      return 'inlineEditDeniedNotAssignable';
    case CPF_INLINE_EDIT_DENY.DAYS_NOT_EDITABLE:
      return 'inlineEditDeniedDaysNotEditable';
    case CPF_INLINE_EDIT_DENY.COLUMN_NOT_ALLOWED:
      return 'inlineEditDeniedColumnNotAllowed';
    case CPF_INLINE_EDIT_DENY.VIEW_LOCKED:
      return 'inlineEditDeniedViewLocked';
    case CPF_INLINE_EDIT_DENY.NOT_DOUBLE_CLICK:
    case CPF_INLINE_EDIT_DENY.NOT_INLINE_MODE:
    default:
      return null;
  }
};
